import { path } from "./endpoints";
import type {
  ActiveResponse,
  ClientConfig,
  GroupPreferences,
  InviteResolved,
  LocationInput,
  Member,
  PlaceSuggestion,
  Problem,
  ResultSet,
  Session,
  SessionCreated,
  StatsResponse,
} from "./types";

// Same origin by default: nginx serves this app and proxies /v1 in production, and
// the Vite dev server proxies it in development. Set VITE_API_BASE only to point at
// an API on another host.
const BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");

/** An error the API described in problem+json, with the fields worth showing. */
export class ApiError extends Error {
  readonly status: number;
  readonly slug: string;
  readonly detail: string;

  constructor(problem: Problem) {
    super(problem.title || `Request failed (${problem.status})`);
    this.name = "ApiError";
    this.status = problem.status;
    this.detail = problem.detail ?? "";
    this.slug = problem.type?.split("/").pop() ?? "unknown";
  }
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("Could not reach the server.");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

async function request<T>(
  url: string,
  init: RequestInit & { token?: string; inviteToken?: string } = {},
): Promise<T> {
  const { token, inviteToken, headers, ...rest } = init;
  let response: Response;
  try {
    response = await fetch(`${BASE}${url}`, {
      ...rest,
      headers: {
        ...(rest.body ? { "Content-Type": "application/json" } : {}),
        ...(token ? { "X-Session-Token": token } : {}),
        ...(inviteToken ? { "X-Invite-Token": inviteToken } : {}),
        ...headers,
      },
    });
  } catch (cause) {
    // A CORS rejection surfaces here as a TypeError with no detail, which is why
    // the backend's allowed origins and headers are covered by tests.
    throw new NetworkError(cause);
  }

  if (response.status === 204) return undefined as T;

  const body = await response.text();
  const parsed: unknown = body ? JSON.parse(body) : undefined;

  if (!response.ok) {
    throw new ApiError(
      (parsed as Problem) ?? {
        type: "about:blank",
        title: response.statusText,
        status: response.status,
        detail: "",
      },
    );
  }
  return parsed as T;
}

export const api = {
  config: () => request<ClientConfig>(path("config")),

  createSession: () =>
    request<SessionCreated>(path("createSession"), { method: "POST" }),

  addMember: (
    sessionId: string,
    token: string,
    body: { name: string; location: LocationInput },
  ) =>
    request<Member>(path("addMember", { session_id: sessionId }), {
      method: "POST",
      token,
      body: JSON.stringify(body),
    }),

  updateMember: (
    sessionId: string,
    token: string,
    memberId: string,
    body: { name: string; location: LocationInput },
  ) =>
    request<Member>(
      path("updateMember", { session_id: sessionId, member_id: memberId }),
      { method: "PATCH", token, body: JSON.stringify(body) },
    ),

  removeMember: (sessionId: string, token: string, memberId: string) =>
    request<void>(
      path("removeMember", { session_id: sessionId, member_id: memberId }),
      { method: "DELETE", token },
    ),

  setPreferences: (sessionId: string, token: string, prefs: GroupPreferences) =>
    request<GroupPreferences>(path("setPreferences", { session_id: sessionId }), {
      method: "PUT",
      token,
      body: JSON.stringify(prefs),
    }),

  compute: (sessionId: string, token: string) =>
    request<ResultSet>(path("compute", { session_id: sessionId }), {
      method: "POST",
      token,
      body: JSON.stringify({ modes: ["drive"] }),
    }),

  refine: (sessionId: string, token: string, prefs: GroupPreferences) =>
    request<ResultSet>(path("refine", { session_id: sessionId }), {
      method: "POST",
      token,
      body: JSON.stringify(prefs),
    }),

  // `near` biases results towards a point - we send the first member's location.
  // Without it, a common place name matches areas in several states at once.
  autocomplete: (
    query: string,
    city?: string,
    near?: { lat: number; lng: number },
  ) => {
    const params = new URLSearchParams({ q: query });
    if (city) params.set("city", city);
    if (near) {
      params.set("near_lat", String(near.lat));
      params.set("near_lng", String(near.lng));
    }
    return request<PlaceSuggestion[]>(`${path("autocomplete")}?${params}`);
  },

  sendFeedback: (venueId: string, workedOut: boolean, groupSize?: number) =>
    request<void>(path("feedback", { venue_id: venueId }), {
      method: "POST",
      body: JSON.stringify({ worked_out: workedOut, group_size: groupSize }),
    }),

  resolveInvite: (inviteToken: string) =>
    request<InviteResolved>(
      path("resolveInvite", { invite_token: inviteToken }),
    ),

  joinSession: (
    sessionId: string,
    inviteToken: string,
    body: { name: string; location: LocationInput },
  ) =>
    request<Member>(path("joinSession", { session_id: sessionId }), {
      method: "POST",
      inviteToken,
      body: JSON.stringify(body),
    }),

  readSessionAsGuest: (sessionId: string, inviteToken: string) =>
    request<Session>(path("readSession", { session_id: sessionId }), {
      inviteToken,
    }),

  computeAsGuest: (sessionId: string, inviteToken: string) =>
    request<ResultSet>(path("compute", { session_id: sessionId }), {
      method: "POST",
      inviteToken,
      body: JSON.stringify({ modes: ["drive"] }),
    }),

  resultsAsGuest: (sessionId: string, inviteToken: string) =>
    request<ResultSet>(path("results", { session_id: sessionId }), {
      inviteToken,
    }),

  destroySession: (sessionId: string, token: string) =>
    request<void>(path("destroySession", { session_id: sessionId }), {
      method: "DELETE",
      token,
    }),

  track: (body: {
    path: string;
    session_id: string;
    referrer?: string;
    utm_source?: string;
    utm_medium?: string;
  }) =>
    request<void>(path("track"), {
      method: "POST",
      body: JSON.stringify(body),
    }),

  adminVerify: (key: string) =>
    request<{ ok: boolean }>(path("adminVerify"), {
      method: "POST",
      headers: { "X-Admin-Key": key },
    }),

  adminStats: (key: string, period: string = "today") =>
    request<StatsResponse>(`${path("adminStats")}?period=${period}`, {
      headers: { "X-Admin-Key": key },
    }),

  adminActive: (key: string) =>
    request<ActiveResponse>(path("adminActive"), {
      headers: { "X-Admin-Key": key },
    }),
};
